// Every Real Estate error follows one of two shapes (docs/real-estate/be/
// 10-api-reference.md §0.5):
//   - a business error:   { response: { status, data: { detail: { code, message } } } }
//   - a validation error: { response: { status, data: { detail: [ {loc, msg, ...}, ... ] } } }
// `message`/`msg` are for logs, never shown to the user (FE handoff §3) —
// every caller should map `code` to its own i18n string instead.

// Returns the machine-readable code for a thrown request error, or
// 'validation' for a plain FastAPI 422 array, or 'network' when there's no
// response at all (offline, CORS, timeout).
export function apiErrorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return 'network';
  if (Array.isArray(detail)) return 'validation';
  return detail.code || 'unknown';
}

// Looks up REAL_ESTATE.ERRORS.<CODE> in i18n; falls back to `fallbackKey`
// (or a generic message) when this particular code has no dedicated copy.
// `vm` is any component instance (has $t/$te bound).
export function apiErrorMessage(vm, err, fallbackKey) {
  const code = apiErrorCode(err);
  const i18nKey = `REAL_ESTATE.ERRORS.${code.toUpperCase()}`;
  if (vm.$te(i18nKey)) {
    return vm.$t(i18nKey);
  }
  if (fallbackKey && vm.$te(fallbackKey)) {
    return vm.$t(fallbackKey);
  }
  return vm.$t('REAL_ESTATE.ERRORS.UNKNOWN');
}

// The server sends 409 `slot_unavailable` both when an agent slot was just
// taken and when a walk-in arrival time falls outside opening hours (or in
// the past); `isWalkIn` picks the copy that matches what actually happened.
export function bookingErrorMessage(vm, err, { isWalkIn, fallbackKey }) {
  if (isWalkIn && apiErrorCode(err) === 'slot_unavailable') {
    return vm.$t('REAL_ESTATE.ERRORS.SLOT_OUTSIDE_HOURS');
  }
  return apiErrorMessage(vm, err, fallbackKey);
}

// Feature/nearby icon uploads also take SVG, so their "wrong type" error
// names it; any other failure reads like every other upload.
export function iconErrorMessage(vm, err) {
  if (apiErrorCode(err) === 'unsupported_image_type') {
    return vm.$t('REAL_ESTATE.ERRORS.UNSUPPORTED_ICON_TYPE');
  }
  return apiErrorMessage(vm, err);
}
